import {
  cancelInvitation,
  fetchAdminInvitations,
  resendInvitation,
} from '@/features/invitations/api/adminInvitations';
import {
  ADMIN_INVITATIONS_KEY,
  ADMIN_INVITATIONS_STALE_TIME,
} from '@/features/invitations/config/adminInvitations';
import type {
  AdminInvitationFilters,
  InvitationCommand,
} from '@/features/invitations/types/adminInvitation';
import { useCommandKey } from '@/shared/hooks/useCommandKey';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export function adminInvitationsQuery(filters: AdminInvitationFilters = {}) {
  const normalizedFilters = {
    organizationId: filters.organizationId?.trim() || undefined,
    q: filters.q?.trim() || undefined,
    status: filters.status,
  };
  return queryOptions({
    queryKey: [ADMIN_INVITATIONS_KEY, normalizedFilters],
    queryFn: ({ signal }) => fetchAdminInvitations(normalizedFilters, { signal }),
    retry: false,
    staleTime: ADMIN_INVITATIONS_STALE_TIME,
  });
}

export function useAdminInvitations(filters: AdminInvitationFilters = {}) {
  return useQuery(adminInvitationsQuery(filters));
}

export function useResendInvitation() {
  const key = useCommandKey();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ newDelivery, ...input }: InvitationCommand & { newDelivery?: boolean }) => {
      if (newDelivery) key.reset();
      return resendInvitation(input, { idempotencyKey: key.reserve(input) });
    },
    retry: false,
    onSuccess: () => {
      key.reset();
      void queryClient.invalidateQueries({ queryKey: [ADMIN_INVITATIONS_KEY] });
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: [ADMIN_INVITATIONS_KEY] });
    },
  });
}

export function useCancelInvitation() {
  const key = useCommandKey();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: InvitationCommand) =>
      cancelInvitation(input, { idempotencyKey: key.reserve(input) }),
    retry: false,
    onSuccess: () => {
      key.reset();
      void queryClient.invalidateQueries({ queryKey: [ADMIN_INVITATIONS_KEY] });
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: [ADMIN_INVITATIONS_KEY] });
    },
  });
}
